import {
  Box,
  CircularProgress,
  Switch,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRequest } from '../../hooks/useRequest';
import {
  AdminTool,
  fetchBuiltinTools,
  fetchMcpTools,
  setToolApproveRequired,
} from '../../utils/axios/api';

type ToolGroups = {
  builtin: AdminTool[];
  mcp: AdminTool[];
};

function ToolSettingsGroup({
  title,
  tools,
  emptyLabel,
  pendingName,
  approveLabel,
  onToggle,
}: {
  title: string;
  tools: AdminTool[];
  emptyLabel: string;
  pendingName: string | null;
  approveLabel: (name: string) => string;
  onToggle: (tool: AdminTool, approveRequired: boolean) => void;
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Typography variant="subtitle1">{title}</Typography>
      {tools.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {emptyLabel}
        </Typography>
      ) : (
        tools.map((tool) => (
          <Box
            key={tool.name}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              px: 1.5,
              py: 1.25,
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2">{tool.name}</Typography>
              {tool.description ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: '-webkit-box',
                    overflow: 'hidden',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 2,
                  }}
                >
                  {tool.description}
                </Typography>
              ) : null}
            </Box>
            <Switch
              checked={tool.approveRequired}
              disabled={pendingName === tool.name}
              slotProps={{
                input: {
                  'aria-label': approveLabel(tool.name),
                },
              }}
              onChange={(_, checked) => {
                onToggle(tool, checked);
              }}
            />
          </Box>
        ))
      )}
    </Box>
  );
}

function updateApproveRequired(
  groups: ToolGroups,
  name: string,
  approveRequired: boolean,
): ToolGroups {
  const mapGroup = (tools: AdminTool[]) =>
    tools.map((item) =>
      item.name === name ? { ...item, approveRequired } : item,
    );

  return {
    builtin: mapGroup(groups.builtin),
    mcp: mapGroup(groups.mcp),
  };
}

export function AdminToolSettingsPage() {
  const { t } = useTranslation();
  const loadRequest = useRequest<ToolGroups>();
  const updateRequest = useRequest<AdminTool>();
  const [tools, setTools] = useState<ToolGroups>({ builtin: [], mcp: [] });
  const [pendingName, setPendingName] = useState<string | null>(null);

  const loadTools = useCallback(async () => {
    const data = await loadRequest.fetch(async () => {
      const [builtin, mcp] = await Promise.all([
        fetchBuiltinTools(),
        fetchMcpTools(),
      ]);
      return { builtin, mcp };
    });
    if (!data) {
      return;
    }
    setTools(data);
  }, [loadRequest]);

  useEffect(() => {
    void loadTools();
  }, [loadTools]);

  const handleToggle = useCallback(
    async (tool: AdminTool, approveRequired: boolean) => {
      setPendingName(tool.name);
      setTools((current) =>
        updateApproveRequired(current, tool.name, approveRequired),
      );

      const updated = await updateRequest.fetch(() =>
        setToolApproveRequired(tool.name, approveRequired),
      );

      setPendingName(null);

      if (!updated) {
        setTools((current) =>
          updateApproveRequired(current, tool.name, tool.approveRequired),
        );
        return;
      }

      setTools((current) =>
        updateApproveRequired(current, tool.name, updated.approveRequired),
      );
    },
    [updateRequest],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        p: 2,
      }}
    >
      <Typography variant="subtitle1">
        {t('admin.toolSettings.description')}
      </Typography>
      {loadRequest.isLoading &&
      tools.builtin.length === 0 &&
      tools.mcp.length === 0 ? (
        <CircularProgress size={24} />
      ) : loadRequest.exception &&
        tools.builtin.length === 0 &&
        tools.mcp.length === 0 ? (
        <Typography variant="body2" color="error">
          {t(`exceptions.${loadRequest.exception}`)}
        </Typography>
      ) : (
        <>
          {updateRequest.exception ? (
            <Typography variant="body2" color="error">
              {t(`exceptions.${updateRequest.exception}`)}
            </Typography>
          ) : null}
          <ToolSettingsGroup
            title={t('admin.toolSettings.builtin')}
            tools={tools.builtin}
            emptyLabel={t('admin.toolSettings.empty')}
            pendingName={pendingName}
            approveLabel={(name) =>
              t('admin.toolSettings.approveRequired', { name })
            }
            onToggle={handleToggle}
          />
          <ToolSettingsGroup
            title={t('admin.toolSettings.mcp')}
            tools={tools.mcp}
            emptyLabel={t('admin.toolSettings.empty')}
            pendingName={pendingName}
            approveLabel={(name) =>
              t('admin.toolSettings.approveRequired', { name })
            }
            onToggle={handleToggle}
          />
        </>
      )}
    </Box>
  );
}
